<template>
  <!--
    UserEditor — the User_Editor create/edit form.

    Presents the editable account fields (email, name, company details, role) and
    the credential. In edit mode the fields are populated from the stored account;
    in create mode the form starts empty.

    Password handling differs between the two modes, deliberately:
      - create: a password is required, since an account with no credential
        cannot sign in.
      - edit:   the password field is optional and starts empty. The stored
        password is never fetched or displayed (the API does not return it), and
        leaving the field blank omits it from the request entirely so the existing
        credential is untouched. Typing a value resets it.

    Client-side validation blocks submission and marks the offending fields
    without sending a request; the server re-validates and remains the authority
    (including the guards that refuse self-demotion and removing the last
    administrator). On a server error the entered values are preserved for
    correction, except the password, which is never echoed back into the form.

    Follows the ProjectEditor conventions: the container owns the snackbars via
    the `saved` query handshake, an in-component `beforeRouteLeave` guard plus a
    `beforeunload` listener prompt before discarding unsaved edits, and Cancel
    navigates back to the list.
  -->
  <v-card class="user-editor" :loading="loading">
    <v-card-title class="user-editor__title">
      {{ isEditMode ? 'Edit user' : 'Add user' }}
    </v-card-title>

    <v-card-text>
      <!-- Load failure for the edit-mode fetch. -->
      <v-alert
        v-if="loadError"
        type="error"
        variant="tonal"
        class="mb-4"
        data-test="load-error"
      >
        {{ loadError }}
        <template #append>
          <v-btn variant="text" size="small" data-test="reload" @click="reload">
            Retry
          </v-btn>
        </template>
      </v-alert>

      <!-- Save failure, including the server's role guards. -->
      <v-alert
        v-if="saveError"
        type="error"
        variant="tonal"
        class="mb-4"
        data-test="save-error"
      >
        {{ saveError }}
      </v-alert>

      <v-form @submit.prevent="save">
        <v-text-field
          v-model="email"
          label="Email"
          type="email"
          color="#e7511e"
          variant="outlined"
          density="comfortable"
          autocomplete="off"
          :error-messages="fieldErrors.email"
          class="mb-2"
          data-test="email"
        />

        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="firstName"
              label="First name"
              color="#e7511e"
              variant="outlined"
              density="comfortable"
              :error-messages="fieldErrors.first_name"
              data-test="first-name"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="lastName"
              label="Last name"
              color="#e7511e"
              variant="outlined"
              density="comfortable"
              :error-messages="fieldErrors.last_name"
              data-test="last-name"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="company"
              label="Company"
              color="#e7511e"
              variant="outlined"
              density="comfortable"
              :error-messages="fieldErrors.company"
              data-test="company"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="companyTitle"
              label="Position"
              color="#e7511e"
              variant="outlined"
              density="comfortable"
              :error-messages="fieldErrors.company_title"
              data-test="company-title"
            />
          </v-col>
        </v-row>

        <v-divider class="my-4" />

        <!-- Role. An explicit select rather than a checkbox so neither value is
             the silent default and the consequence of each is spelled out. -->
        <v-select
          v-model="role"
          :items="roleOptions"
          item-title="title"
          item-value="value"
          label="Role"
          color="#e7511e"
          variant="outlined"
          density="comfortable"
          :error-messages="fieldErrors.role"
          :hint="roleHint"
          persistent-hint
          class="mb-4"
          data-test="role"
        />

        <!-- Warning shown when an administrator edits their OWN account and
             tries to drop their access. The server refuses this outright; saying
             so here avoids a pointless round trip. -->
        <v-alert
          v-if="isSelfDemotion"
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-4"
          data-test="self-demotion-warning"
        >
          You cannot reduce your own access level. Ask another administrator to
          change your role.
        </v-alert>

        <v-divider class="my-4" />

        <v-text-field
          v-model="password"
          :label="isEditMode ? 'New password (leave blank to keep current)' : 'Password'"
          type="password"
          color="#e7511e"
          variant="outlined"
          density="comfortable"
          autocomplete="new-password"
          :error-messages="fieldErrors.password"
          :hint="passwordHint"
          persistent-hint
          class="mb-4"
          data-test="password"
        />

        <v-divider class="my-4" />

        <div class="d-flex ga-3 justify-end">
          <v-btn
            variant="text"
            :disabled="saving"
            data-test="cancel"
            @click="cancel"
          >
            Cancel
          </v-btn>
          <v-btn
            type="submit"
            color="#e7511e"
            :loading="saving"
            :disabled="loading || isSelfDemotion"
            data-test="save"
          >
            {{ isEditMode ? 'Save changes' : 'Create user' }}
          </v-btn>
        </div>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
// UserEditor — User_Editor create/edit form.
import { mapStores } from 'pinia'
import AdminUserApi from 'services/AdminUserApi'
import { useAuthStore } from '@/stores/auth'
import {
  ROLE_SUPERADMIN,
  ROLE_ADMIN,
  ROLE_EMPLOYEE,
  ROLES,
  rankOf,
  labelForRole,
  resolveRole,
  assignableRoles
} from 'services/roles'

// Must match the server's MIN_PASSWORD_LENGTH in userValidation.js. Duplicated
// rather than shared because the client and server are separate npm packages
// with no shared module; the server re-validates regardless, so the client copy
// is a convenience that fails safe if it ever drifts low.
const MIN_PASSWORD_LENGTH = 8

// Pragmatic structural email check for immediate feedback. The server performs
// the authoritative validation (joi), so this only needs to catch obvious typos
// without rejecting valid internal domains.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function extractErrorMessage (err, fallback) {
  const data = err && err.response && err.response.data
  if (data) {
    if (typeof data.message === 'string' && data.message) return data.message
    if (typeof data.error === 'string' && data.error) return data.error
    if (data.fields && typeof data.fields === 'object') {
      const first = Object.keys(data.fields)[0]
      if (first && typeof data.fields[first] === 'string') return data.fields[first]
    }
  }
  return fallback
}

// Snapshot of the tracked form fields, used for unsaved-changes detection. The
// password is included so typing a new one counts as a change.
function captureBaseline (state) {
  return JSON.stringify(state)
}

export default {
  name: 'UserEditor',

  props: {
    // Present in edit mode (from the `:id` route param via `props: true`).
    id: {
      type: [String, Number],
      default: null
    }
  },

  emits: ['saved', 'save-error', 'dirty-change', 'cancel'],

  data () {
    return {
      email: '',
      firstName: '',
      lastName: '',
      company: '',
      companyTitle: '',
      // New accounts default to the least-privileged role, so an administrator
      // has to deliberately choose to grant admin access.
      role: ROLE_EMPLOYEE,
      password: '',

      // The stored role as loaded, used to detect a self-demotion attempt.
      loadedRole: null,
      loadedPublicId: null,

      loading: false,
      loadError: '',
      saving: false,
      saveError: '',
      fieldErrors: {},

      baseline: null,
      lastDirty: false
    }
  },

  computed: {
    ...mapStores(useAuthStore),

    userId () {
      return this.id
    },

    isEditMode () {
      return this.userId !== null && this.userId !== undefined && this.userId !== ''
    },

    // Role options this user may assign, plus the account's current role so the
    // select always has a displayable value. Mirrors the server's rule that you
    // cannot grant an access level above your own.
    roleOptions () {
      const values = assignableRoles(this.authStore.role)
      if (this.loadedRole && values.indexOf(this.loadedRole) === -1) {
        values.push(this.loadedRole)
      }
      return values
        .slice()
        .sort((a, b) => rankOf(a) - rankOf(b))
        .map((value) => ({ title: labelForRole(value), value }))
    },

    roleHint () {
      switch (this.role) {
        case ROLE_SUPERADMIN:
          return 'Full access, including any features reserved for super administrators.'
        case ROLE_ADMIN:
          return 'Can manage users, roles, and projects.'
        default:
          return 'Can sign in, but cannot manage users or roles.'
      }
    },

    passwordHint () {
      return this.isEditMode
        ? `Leave blank to keep the current password. Minimum ${MIN_PASSWORD_LENGTH} characters if changed.`
        : `Minimum ${MIN_PASSWORD_LENGTH} characters.`
    },

    // True when the signed-in user is editing their OWN account and has selected
    // a lower-ranked role. The server refuses any self-demotion, so the form
    // disables saving and the attempt is caught before a request is sent.
    isSelfDemotion () {
      if (!this.isEditMode) return false
      const profile = this.authStore.profile
      const currentPublicId = (profile && profile.public_id) || null
      if (!currentPublicId || !this.loadedPublicId) return false
      if (currentPublicId !== this.loadedPublicId) return false
      return rankOf(this.role) < rankOf(this.loadedRole)
    },

    currentFormState () {
      return {
        email: this.email,
        firstName: this.firstName,
        lastName: this.lastName,
        company: this.company,
        companyTitle: this.companyTitle,
        role: this.role,
        password: this.password
      }
    },

    isDirty () {
      if (this.baseline === null) return false
      return captureBaseline(this.currentFormState) !== this.baseline
    }
  },

  watch: {
    isDirty (value) {
      if (value !== this.lastDirty) {
        this.lastDirty = value
        this.$emit('dirty-change', value)
      }
    }
  },

  created () {
    this.init()
  },

  mounted () {
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },

  beforeUnmount () {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },

  // In-component navigation guard. Prompts before leaving while the form holds
  // unsaved changes; a successful save re-baselines so it does not prompt.
  beforeRouteLeave (to, from, next) {
    if (this.isDirty && !this.confirmDiscard()) {
      next(false)
      return
    }
    next()
  },

  methods: {
    async init () {
      if (this.isEditMode) {
        await this.loadUser()
      } else {
        this.baseline = captureBaseline(this.currentFormState)
      }
    },

    async loadUser () {
      this.loading = true
      this.loadError = ''

      try {
        const res = await AdminUserApi.get(this.userId)
        const user = (res && res.data && (res.data.user || res.data)) || {}

        this.email = user.email || ''
        this.firstName = user.first_name || ''
        this.lastName = user.last_name || ''
        this.company = user.company || ''
        this.companyTitle = user.company_title || ''
        // Resolve through the shared helper so the authoritative `role` wins and
        // a legacy payload carrying only `is_admin` still loads sensibly.
        this.role = resolveRole(user)
        this.loadedRole = this.role
        this.loadedPublicId = user.public_id || null
        // Never populated from the server: the API does not return credentials.
        this.password = ''

        this.baseline = captureBaseline(this.currentFormState)
      } catch (err) {
        this.loadError = extractErrorMessage(err, 'Unable to load the user. Please try again.')
      } finally {
        this.loading = false
      }
    },

    reload () {
      if (this.isEditMode) {
        this.loadUser()
      }
    },

    // Client-side validation. Marks the offending fields and blocks the request.
    validate () {
      const errors = {}

      const email = (this.email || '').trim()
      if (email === '') {
        errors.email = 'Email is required.'
      } else if (!EMAIL_PATTERN.test(email)) {
        errors.email = 'Enter a valid email address.'
      }

      if (ROLES.indexOf(this.role) === -1) {
        errors.role = 'Select a role.'
      } else if (rankOf(this.role) > rankOf(this.authStore.role)) {
        // Mirrors the server's anti-escalation rule, so the attempt is reported
        // here rather than coming back as a 422.
        errors.role = 'You cannot grant an access level higher than your own.'
      }

      const password = this.password || ''
      if (!this.isEditMode) {
        // Required on create: an account with no credential cannot sign in.
        if (password.length < MIN_PASSWORD_LENGTH) {
          errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`
        }
      } else if (password !== '' && password.length < MIN_PASSWORD_LENGTH) {
        // Optional on edit, but must meet the policy once anything is typed.
        errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`
      }

      this.fieldErrors = errors
      return Object.keys(errors).length === 0
    },

    async save () {
      this.saveError = ''

      if (this.isSelfDemotion) {
        this.saveError =
          'You cannot reduce your own access level. Ask another administrator to change your role.'
        return
      }

      if (!this.validate()) {
        return
      }

      this.saving = true
      try {
        const payload = {
          email: (this.email || '').trim(),
          first_name: (this.firstName || '').trim(),
          last_name: (this.lastName || '').trim(),
          company: (this.company || '').trim(),
          company_title: (this.companyTitle || '').trim(),
          role: this.role
        }

        // Only send a password when one was actually entered. Omitting the key
        // entirely (rather than sending '') is what tells the server to leave the
        // existing credential alone.
        const password = this.password || ''
        if (password !== '') {
          payload.password = password
        }

        let user
        if (this.isEditMode) {
          const res = await AdminUserApi.update(this.userId, payload)
          user = (res && res.data && (res.data.user || res.data)) || {}
        } else {
          const res = await AdminUserApi.create(payload)
          user = (res && res.data && (res.data.user || res.data)) || {}
        }

        // Clear the credential from component state now it is persisted, and
        // re-baseline so leaving does not prompt.
        this.password = ''
        this.loadedRole = this.role
        this.baseline = captureBaseline(this.currentFormState)

        this.$emit('saved', user)

        // Return to the list with the flag the container turns into a
        // confirmation snackbar.
        this.$router.push({
          name: 'admin-users',
          query: { saved: this.isEditMode ? 'updated' : 'created' }
        })
      } catch (err) {
        // Preserve the entered values for correction, and surface the server's
        // message (which includes the role-guard refusals). The password is
        // cleared rather than retained so a rejected save never leaves a
        // credential sitting in the form.
        this.saveError = extractErrorMessage(err, 'Unable to save the user. Please try again.')
        const fields = err && err.response && err.response.data && err.response.data.fields
        if (fields && typeof fields === 'object') {
          this.fieldErrors = { ...this.fieldErrors, ...fields }
        }
        this.$emit('save-error', this.saveError)
      } finally {
        this.saving = false
      }
    },

    cancel () {
      this.$emit('cancel')
      // Return to the User_List. beforeRouteLeave still guards this navigation
      // when the form is dirty.
      this.$router.push({ name: 'admin-users' })
    },

    confirmDiscard () {
      if (typeof window === 'undefined' || typeof window.confirm !== 'function') {
        return true
      }
      return window.confirm(
        'You have unsaved changes that will be lost. Are you sure you want to leave?'
      )
    },

    handleBeforeUnload (event) {
      if (!this.isDirty) return undefined
      event.preventDefault()
      event.returnValue = ''
      return ''
    }
  }
}
</script>

<style scoped>
.user-editor {
  /*
    Clear the fixed Vuetify v-app-bar, matching the other admin screens. The app
    shell renders page content in a plain <main> (not <v-main>), so content is
    not automatically offset beneath the app bar.
  */
  margin-top: 64px;
}

.user-editor__title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #41281b;
}
</style>
